import { useRef } from "react";
import { motion, useInView } from "framer-motion";

const POINTS = [
    { x: 40, y: 230 }, { x: 110, y: 215 }, { x: 190, y: 200 }, { x: 270, y: 195 },
    { x: 350, y: 175 }, { x: 430, y: 165 }, { x: 510, y: 135 }, { x: 590, y: 110 },
    { x: 670, y: 95 }, { x: 750, y: 70 }, { x: 830, y: 50 }, { x: 910, y: 30 },
];

const PATH = POINTS.reduce(
    (acc, p, i) => acc + (i === 0 ? `M ${p.x},${p.y}` : ` L ${p.x},${p.y}`),
    ""
);

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

export default function Results() {
    const ref = useRef(null);
    const inView = useInView(ref, { amount: 0.35, once: true });

    return (
        <section
            id="results"
            ref={ref}
            data-testid="results-section"
            className="relative text-white py-20 lg:py-32"
            style={{ backgroundColor: "#0d3a6e" }}
        >
            <div className="mx-auto max-w-[1440px] px-4 sm:px-6 lg:px-10 grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
                <div className="lg:col-span-5">
                    <p className="label-eyebrow-light">Results</p>
                    <motion.h2
                        initial={{ opacity: 0, y: 30 }}
                        animate={inView ? { opacity: 1, y: 0 } : {}}
                        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                        className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-[0.95]"
                    >
                        FROM STRATEGY
                        <br />
                        <span className="font-serif-it text-communiti-soft">
                            TO REAL IMPACT
                        </span>
                    </motion.h2>
                    <p className="mt-8 max-w-md text-base text-white/70 leading-relaxed">
                        From strategy to execution, we deliver measurable growth. At
                        Commmmuniti, the best digital marketing agency in Indore, we
                        create data-driven campaigns that drive growth, engagement, and
                        conversions.
                    </p>
                </div>

                <div className="lg:col-span-7 relative">
                    <div className="rounded-3xl border border-white/10 bg-white/[0.02] p-6 sm:p-10">
                        <div className="relative w-full">
                            <svg
                                viewBox="0 0 960 260"
                                className="w-full h-auto"
                                preserveAspectRatio="none"
                            >
                                {[0, 1, 2, 3].map((g) => (
                                    <line
                                        key={g}
                                        x1="0"
                                        y1={60 * g + 30}
                                        x2="960"
                                        y2={60 * g + 30}
                                        stroke="rgba(255,255,255,0.06)"
                                        strokeWidth="1"
                                    />
                                ))}

                                <motion.path
                                    d={`${PATH} L 910,260 L 40,260 Z`}
                                    fill="url(#g)"
                                    initial={{ opacity: 0 }}
                                    animate={inView ? { opacity: 1 } : {}}
                                    transition={{ delay: 1.6, duration: 0.8 }}
                                />
                                <defs>
                                    <linearGradient id="g" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="#1F54A3" stopOpacity="0.45" />
                                        <stop offset="100%" stopColor="#0F3D7A" stopOpacity="0" />
                                    </linearGradient>
                                </defs>

                                <motion.path
                                    d={PATH}
                                    fill="none"
                                    stroke="#1F54A3"
                                    strokeWidth="2.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    initial={{ pathLength: 0 }}
                                    animate={inView ? { pathLength: 1 } : {}}
                                    transition={{ duration: 1.8, ease: [0.16, 1, 0.3, 1] }}
                                />

                                <motion.circle
                                    cx={POINTS[POINTS.length - 1].x}
                                    cy={POINTS[POINTS.length - 1].y}
                                    r="6"
                                    fill="#1F54A3"
                                    initial={{ scale: 0 }}
                                    animate={inView ? { scale: 1 } : {}}
                                    transition={{ delay: 1.8, duration: 0.4 }}
                                />
                                <motion.circle
                                    cx={POINTS[POINTS.length - 1].x}
                                    cy={POINTS[POINTS.length - 1].y}
                                    r="14"
                                    fill="none"
                                    stroke="#1F54A3"
                                    strokeOpacity="0.4"
                                    initial={{ scale: 0 }}
                                    animate={inView ? { scale: 1 } : {}}
                                    transition={{ delay: 1.9, duration: 0.5 }}
                                />
                            </svg>
                        </div>

                        <div className="mt-4 grid grid-cols-12 text-[10px] tracking-widest text-white/40">
                            {MONTHS.map((m) => (
                                <span key={m} className="text-center">
                                    {m}
                                </span>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
